import React, { MouseEvent } from 'react'
import Tooltip from 'components/Tooltip'
import { DocumentDownloadableAssetsFragment } from 'gqlTypes'
import useAssetDownload, { AssetState } from './../useAssetDownload'
import { formatByteCount } from 'utils'
import Spinner from 'components/Spinner'
import Box, { Flex } from 'components/Box'
import {
  AddLibraryIcon,
  ByteCount,
  DownloadDocumentIcon,
  Error,
  ExportAssetsIcon,
  OpenInSketchIcon,
  SidebarButton,
  SidebarAnchor,
  DownloadSectionItem,
} from './DownloadSection.styles'
import { downloadFile } from 'components/DownloadFile'

interface ButtonWithOnClick {
  onClick: React.MouseEventHandler
  disabled?: boolean
}

interface ButtonWithHref {
  href: string
}

const AddToLibraryButton: React.FC<ButtonWithOnClick> = ({ onClick }) => (
  <SidebarButton
    onClick={onClick}
    data-testid="share-meta-sidebar-library-button">
    <div>Add Library</div>
    <AddLibraryIcon />
  </SidebarButton>
)

const OpenInSketchButton: React.FC<ButtonWithOnClick> = ({
  onClick,
  disabled = false,
  ...props
}) => (
  <SidebarButton onClick={onClick} disabled={disabled} {...props}>
    <div>Edit</div>
    <OpenInSketchIcon />
  </SidebarButton>
)

const DownloadDocumentButton: React.FC<ButtonWithHref> = ({ href }) => (
  <SidebarAnchor
    small
    onClick={(e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      e.preventDefault()
      downloadFile(href)
    }}
    href={href}
    data-testid="share-meta-sidebar-download-button">
    <div>Download Document</div>
    <DownloadDocumentIcon />
  </SidebarAnchor>
)

interface DownloadAssetsButtonProps {
  fileSizeInBytes: number | null
  assetPath: string
}

const DownloadAssetsButton: React.FC<DownloadAssetsButtonProps> = ({
  fileSizeInBytes,
  assetPath,
}) => {
  const formattedSize = fileSizeInBytes
    ? formatByteCount(fileSizeInBytes, { fractionDigits: 0 })
    : ''

  return (
    <SidebarAnchor
      small
      fill
      href={assetPath}
      onClick={(e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.preventDefault()
        downloadFile(assetPath)
      }}>
      <span>Download Assets</span>
      <Flex alignItems="center">
        <Box marginRight={3}>
          <ByteCount>ZIP {formattedSize}</ByteCount>
        </Box>
        <ExportAssetsIcon />
      </Flex>
    </SidebarAnchor>
  )
}

interface ExportAssetsButtonProps {
  document?: DocumentDownloadableAssetsFragment
  assetState: AssetState
  contentWrapper?: React.FC
}

const isForbiddenError = (assetState: AssetState): boolean => {
  if (!('errors' in assetState)) {
    return false
  }

  const errors = assetState.errors
  if (!errors) {
    return false
  }

  return !!errors.find(error => error.code === 'FORBIDDEN')
}

const ExportAssetsButton: React.FC<ExportAssetsButtonProps> = ({
  document,
  assetState,
}) => {
  const assetsAvailable = `request` in assetState

  return (
    <>
      <Tooltip
        placement="top"
        disabled={document?.assetStatus === 'AVAILABLE'}
        content="No assets to export. Set layers as exportable or draw slices in Sketch.">
        <SidebarButton
          small={true}
          fill={true}
          disabled={!assetsAvailable || assetState.type === 'LOADING'}
          download
          onClick={() => 'request' in assetState && assetState.request?.()}>
          <div>
            {assetState.type === 'LOADING'
              ? 'Preparing Assets…'
              : 'Export Assets'}
          </div>
          {assetState.type === 'LOADING' ? (
            <Spinner primary size="16px" />
          ) : (
            <ExportAssetsIcon />
          )}
        </SidebarButton>
      </Tooltip>
      {assetState.type === 'RECOVERABLE_FAILURE' && (
        <Error>
          <>
            Failed to export assets, please try again.
            <br />
            If this error persists, please try uploading your document again.
          </>
        </Error>
      )}
      {assetState.type === 'PERMANENT_FAILURE' &&
        (isForbiddenError(assetState) ? (
          <Error>
            <>
              The option to export assets has been disabled in this document’s
              settings. Please contact the document’s owner for assistance.
            </>
          </Error>
        ) : (
          <Error>
            <>
              Failed to export assets.
              <br />
              Please try uploading your document again.
            </>
          </Error>
        ))}
    </>
  )
}

interface DownloadOrExportButtonProps {
  document?: DocumentDownloadableAssetsFragment
  shareIdentifier: string
  versionShortId?: string
}

const DownloadOrExportAssetsButton: React.FC<DownloadOrExportButtonProps> = ({
  document,
  shareIdentifier,
  versionShortId,
}) => {
  const assetState = useAssetDownload({
    document,
    shareIdentifier,
    versionShortId,
  })

  const asset =
    'assets' in assetState &&
    assetState.assets.length > 0 &&
    assetState.assets[0]

  return (
    <>
      {asset && asset.path ? (
        <DownloadAssetsButton
          fileSizeInBytes={asset.fileSizeInBytes}
          assetPath={asset.path}
        />
      ) : (
        <ExportAssetsButton document={document} assetState={assetState} />
      )}
    </>
  )
}

export {
  AddToLibraryButton,
  DownloadOrExportAssetsButton,
  OpenInSketchButton,
  DownloadDocumentButton,
  DownloadSectionItem,
}
